import { ColDef, ColGroupDef } from '@ag-grid-community/core';
import { formatColumnDate, dateFilterParams, formatNumber, getPureNumber, formatPrice } from '../lib/utils';

const defaultFilterParams = {
  newRowsAction: 'keep',
};
export default [
  {
    cellClass: 'ticker-cell',
    field: 'ex_dividend_date',
    filter: 'agDateColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      ...dateFilterParams,
      buttons: ['clear'],
      inRangeInclusive: true,
    },
    headerName: 'Ex-Date',
    initialWidth: 80,
    valueFormatter: ({ data }) => formatColumnDate(data.ex_dividend_date),
    valueGetter: ({ data }) => {
      if (!data.ex_dividend_date) return null;

      return data.ex_dividend_date;
    },
  },
  {
    cellClass: 'ticker-cell',
    cellRenderer: 'tickerWidgetCell',
    field: 'ticker',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    initialWidth: 60,
  },
  {
    cellClass: 'ticker-cell',
    cellRenderer: ({ data }: any) => {
      return (
        <div className="company-name-cell">
          <span>{data.name}</span>
          {data.logo && <img alt="" className="bz-ag-table__company-image" loading="lazy" src={data.logo}></img>}
        </div>
      );
    },
    cellRendererParams: {
      hideOnInitialTicker: true,
    },
    field: 'name',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    headerName: 'Company',
    initialWidth: 160,
  },
  {
    cellClass: 'ticker-cell',
    field: 'frequency',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    headerName: 'Payments per year',
    initialWidth: 120,
    valueGetter: ({ data }) => {
      if (!data.frequency) return 'Special';

      return data.frequency;
    },
  },
  {
    cellClass: 'ticker-cell',
    field: 'dividend',
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
      values: [
        {
          label: '$0 - $0.50',
          rules: (value: number) => {
            return value >= 0 && value <= 0.5;
          },
        },
        {
          label: '$0.51 - $1.00',
          rules: (value: number) => {
            return value >= 0.51 && value <= 1.0;
          },
        },
        {
          label: '$1.01 - $5.00',
          rules: (value: number) => {
            return value >= 1.01 && value <= 5.0;
          },
        },
        {
          label: '$5.01 - $10.00',
          rules: (value: number) => {
            return value >= 5.01 && value <= 10.0;
          },
        },
        {
          label: 'Greater then $10.00',
          rules: (value: number) => {
            return value >= 10.0;
          },
        },
      ],
    },
    headerName: 'Dividend',
    initialWidth: 100,
    valueFormatter: ({ value }) => formatPrice(value),
    valueGetter: ({ data }) => getPureNumber(data.dividend),
  },
  {
    cellClass: 'ticker-cell',
    field: 'dividend_yield',
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
      values: [
        {
          label: '0-1%',
          rules: (value: number) => {
            return value >= 0 && value <= 1;
          },
        },
        {
          label: '1-5%',
          rules: (value: number) => {
            return value >= 1 && value <= 5;
          },
        },
        {
          label: '5-10%',
          rules: (value: number) => {
            return value >= 5 && value <= 10;
          },
        },
        {
          label: '10%+',
          rules: (value: number) => {
            return value >= 10;
          },
        },
      ],
    },
    headerName: 'Yield',
    initialWidth: 100,
    valueFormatter: ({ value }) => {
      if (!value) return '-';

      return `${value}%`;
    },
    valueGetter: ({ data }) => {
      if (!data.dividend_yield) return 0;

      return Number(formatNumber(data.dividend_yield * 100));
    },
  },
  {
    cellClass: 'ticker-cell',
    field: 'date',
    filter: 'agDateColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      ...dateFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
      inRangeInclusive: true,
    },
    headerName: 'Announced',
    initialWidth: 100,
    valueFormatter: ({ data }) => formatColumnDate(data.date),
    valueGetter: ({ data }) => {
      if (!data.date) return null;

      return data.date;
    },
  },
  {
    cellClass: 'ticker-cell',
    field: 'record_date',
    filter: 'agDateColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      ...dateFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
      inRangeInclusive: true,
    },
    headerName: 'Record',
    initialWidth: 100,
    valueFormatter: ({ data }) => formatColumnDate(data.record_date),
    valueGetter: ({ data }) => {
      if (!data.record_date) return null;

      return data.record_date;
    },
  },
  {
    cellClass: 'ticker-cell',
    field: 'payable_date',
    filter: 'agDateColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      ...dateFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
      inRangeInclusive: true,
    },
    headerName: 'Payable',
    initialWidth: 100,
    valueFormatter: ({ data }) => formatColumnDate(data.payable_date),
    valueGetter: ({ data }) => {
      if (!data.payable_date) return null;

      return data.payable_date;
    },
  },
  {
    cellClass: 'ticker-cell',
    cellRenderer: () => {
      return (
        // eslint-disable-next-line react/jsx-no-target-blank
        <a href="https://www.benzinga.com/profile/portfolio/?action=login&utm_source=calendars" target="_blank">
          Get Alert
        </a>
      );
    },
    field: 'get_alert',
    headerName: 'Get Alert',
    initialWidth: 70,
    suppressAutoSize: true,
    suppressMenu: true,
    suppressSizeToFit: true,
  },
] as (ColDef | ColGroupDef)[];
